import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import FacebookRoundedIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import WhatshotRoundedIcon from "@mui/icons-material/WhatshotRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import {
  getSocialMediaSuggestions,
  getSocialMediaPlan,
  createSocialMediaPlanItem,
  updateSocialMediaPlanItem,
  deleteSocialMediaPlanItem,
} from "../../api/socialMediaService";
import { formatCurrency, formatDate } from "./constants";
import { PeriodSelector, SectionCard, EmptyState, IconBadge } from "./analyticsShared";

const PLATFORM_LABELS = { 1: "Facebook", 2: "Instagram", 3: "TikTok" };
const PLATFORM_BY_NAME = { Facebook: 1, Instagram: 2, TikTok: 3 };
const PLATFORM_ICONS = { Facebook: FacebookRoundedIcon, Instagram: InstagramIcon, TikTok: MusicNoteRoundedIcon };
const PLATFORM_COLORS = { Facebook: "#1877F2", Instagram: "#C13584", TikTok: "#111111" };
const ALL_PLATFORMS = ["Facebook", "Instagram", "TikTok"];

const POST_TYPE_LABELS = { 1: "Post", 2: "Story", 3: "Reel", 4: "Ad" };
const POST_TYPE_BY_NAME = { Post: 1, Story: 2, Reel: 3, Ad: 4 };

const STATUS_LABELS = { 1: "Planned", 2: "Posted", 3: "Skipped" };
const STATUS_COLORS = { 1: "warning", 2: "success", 3: "default" };

const SUGGESTION_TABS = [
  { value: "bestsellers", label: "Bestsellers", icon: WhatshotRoundedIcon, color: "warning" },
  { value: "trending", label: "Trending", icon: TrendingUpRoundedIcon, color: "success" },
  { value: "mostWanted", label: "Most wanted", icon: FavoriteRoundedIcon, color: "error" },
  { value: "needsAPush", label: "Needs a push", icon: RocketLaunchRoundedIcon, color: "info" },
];

function PlatformChip({ name, size = "small" }) {
  const Icon = PLATFORM_ICONS[name];
  return (
    <Chip
      size={size}
      icon={Icon ? <Icon sx={{ fontSize: 15 }} /> : undefined}
      label={name}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        color: PLATFORM_COLORS[name],
        borderColor: PLATFORM_COLORS[name],
        bgcolor: "transparent",
      }}
      variant="outlined"
    />
  );
}

function toLocalDateTimeInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function SocialMedia() {
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [activeTab, setActiveTab] = useState("bestsellers");
  const [snackbar, setSnackbar] = useState("");

  const [planItems, setPlanItems] = useState([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [planStatusFilter, setPlanStatusFilter] = useState("");
  const [planPlatformFilter, setPlanPlatformFilter] = useState("");
  const [planRefreshToken, setPlanRefreshToken] = useState(0);

  const [dialogState, setDialogState] = useState(null); // { suggestion, defaultPostType, defaultBudget }

  const customIncomplete = period === "custom" && (!customStart || !customEnd);

  useEffect(() => {
    if (customIncomplete) return;
    let active = true;
    setLoading(true);
    getSocialMediaSuggestions({
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
    })
      .then((result) => {
        if (active) {
          setData(result);
          setError("");
          setLastUpdated(new Date());
        }
      })
      .catch(() => {
        if (active) setError("Could not load social media suggestions. Is the API running?");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [period, customStart, customEnd, customIncomplete, refreshToken]);

  const loadPlan = useCallback(() => {
    let active = true;
    setPlanLoading(true);
    getSocialMediaPlan({
      status: planStatusFilter || undefined,
      platform: planPlatformFilter || undefined,
    })
      .then((result) => {
        if (active) setPlanItems(result);
      })
      .catch(() => {
        if (active) setSnackbar("Could not load the content plan.");
      })
      .finally(() => {
        if (active) setPlanLoading(false);
      });
    return () => {
      active = false;
    };
  }, [planStatusFilter, planPlatformFilter]);

  useEffect(() => loadPlan(), [loadPlan, planRefreshToken]);

  const handleRefresh = useCallback(() => {
    if (!loading) setRefreshToken((t) => t + 1);
  }, [loading]);

  const handleCopy = useCallback((text) => {
    navigator.clipboard?.writeText(text).then(
      () => setSnackbar("Caption copied to clipboard."),
      () => setSnackbar("Could not copy - copy it manually.")
    );
  }, []);

  const openAddDialog = useCallback((suggestion, defaultPostType = "Post", defaultBudget) => {
    setDialogState({ suggestion, defaultPostType, defaultBudget });
  }, []);

  const handleCreated = useCallback(() => {
    setDialogState(null);
    setPlanRefreshToken((t) => t + 1);
    setSnackbar("Added to the content plan.");
  }, []);

  const handleStatusChange = useCallback(async (item, status) => {
    try {
      await updateSocialMediaPlanItem(item.socialMediaPostId, { status });
      setPlanRefreshToken((t) => t + 1);
    } catch {
      setSnackbar("Could not update that item.");
    }
  }, []);

  const handleDelete = useCallback(async (item) => {
    try {
      await deleteSocialMediaPlanItem(item.socialMediaPostId);
      setPlanRefreshToken((t) => t + 1);
      setSnackbar("Removed from the content plan.");
    } catch {
      setSnackbar("Could not delete that item.");
    }
  }, []);

  const isRefreshing = loading && !!data;
  const activeList = data?.[activeTab] ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
              width: 44,
              height: 44,
              boxShadow: (theme) => `0 8px 20px ${theme.palette.primary.main}33`,
            }}
          >
            <ShareRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Social Media</Typography>
            <Typography variant="body2" color="text.secondary">
              Analytics-driven picks for what to post, when to post it, and what to boost.
            </Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
          <PeriodSelector
            period={period}
            onPeriodChange={setPeriod}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={(start, end) => {
              setCustomStart(start);
              setCustomEnd(end);
            }}
          />
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: "flex-end", sm: "center" } }}>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary" noWrap>
                Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={handleRefresh} disabled={loading || customIncomplete}>
                  <RefreshRoundedIcon
                    fontSize="small"
                    sx={
                      isRefreshing
                        ? { animation: "sm-spin 0.8s linear infinite", "@keyframes sm-spin": { to: { transform: "rotate(360deg)" } } }
                        : undefined
                    }
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ height: 3, mb: 2, borderRadius: 1, overflow: "hidden" }}>
        <Fade in={isRefreshing} unmountOnExit>
          <LinearProgress sx={{ height: 3 }} />
        </Fade>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {customIncomplete ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, color: "text.secondary" }}>
          <EventRoundedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">Pick a start and end date to load custom range suggestions.</Typography>
        </Box>
      ) : loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Box sx={{ mb: 3 }}>
            <BestTimeToPostCard windows={data.bestPostingWindows} />
          </Box>

          <SectionCard
            title="What to post"
            subtitle="Four angles on your own analytics, each with a ready-to-edit caption"
            action={<IconBadge icon={CampaignRoundedIcon} color="primary" />}
          >
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 36, mb: 2, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 600 } }}
            >
              {SUGGESTION_TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={`${t.label} (${data[t.value]?.length ?? 0})`} icon={<t.icon sx={{ fontSize: 18 }} />} iconPosition="start" />
              ))}
            </Tabs>

            {activeList.length === 0 ? (
              <EmptyState text="No products fit this angle for this period yet." icon={Inventory2RoundedIcon} />
            ) : (
              <Grid container spacing={2}>
                {activeList.map((s) => (
                  <Grid key={s.productId} size={{ xs: 12, md: 6, lg: 4 }}>
                    <ProductSuggestionCard suggestion={s} onCopy={handleCopy} onAddToPlan={() => openAddDialog(s)} />
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>

          <Box sx={{ mt: 3 }}>
            <SectionCard
              title="Worth a promoted ad"
              subtitle="High-interest products where paid reach on Facebook/Instagram/TikTok should pay off"
              action={<IconBadge icon={RocketLaunchRoundedIcon} color="error" />}
            >
              {!data.adSuggestions || data.adSuggestions.length === 0 ? (
                <EmptyState text="Nothing stands out for a paid boost this period." icon={RocketLaunchRoundedIcon} />
              ) : (
                <Grid container spacing={2}>
                  {data.adSuggestions.map((s) => (
                    <Grid key={s.productId} size={{ xs: 12, md: 6, lg: 4 }}>
                      <ProductSuggestionCard
                        suggestion={s}
                        onCopy={handleCopy}
                        isAd
                        onAddToPlan={() =>
                          openAddDialog(s, "Ad", s.suggestedAdBudgetMin && s.suggestedAdBudgetMax ? Math.round((s.suggestedAdBudgetMin + s.suggestedAdBudgetMax) / 2) : undefined)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </SectionCard>
          </Box>

          <Box sx={{ mt: 3 }}>
            <ContentPlanSection
              items={planItems}
              loading={planLoading}
              statusFilter={planStatusFilter}
              platformFilter={planPlatformFilter}
              onStatusFilterChange={setPlanStatusFilter}
              onPlatformFilterChange={setPlanPlatformFilter}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
              onAddCustom={() => openAddDialog(null)}
            />
          </Box>
        </>
      ) : null}

      {dialogState && (
        <AddToPlanDialog
          suggestion={dialogState.suggestion}
          defaultPostType={dialogState.defaultPostType}
          defaultBudget={dialogState.defaultBudget}
          onClose={() => setDialogState(null)}
          onCreated={handleCreated}
        />
      )}

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar("")} message={snackbar} />
    </Box>
  );
}

function BestTimeToPostCard({ windows }) {
  return (
    <SectionCard
      title="Best time to post"
      subtitle="When your visitors are actually browsing, so organic posts catch them live"
      action={<IconBadge icon={ScheduleRoundedIcon} color="secondary" />}
    >
      {!windows || windows.length === 0 ? (
        <EmptyState text="Not enough activity in this period to suggest a posting window yet." icon={ScheduleRoundedIcon} />
      ) : (
        <Grid container spacing={2}>
          {windows.map((w, i) => (
            <Grid key={w.label} size={{ xs: 12, sm: 4 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: i === 0 ? "rgba(18,184,134,0.06)" : "transparent",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {i === 0 && <Chip size="small" label="Best" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />}
                  <Typography variant="h6" fontWeight={800}>
                    {w.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {w.daysOfWeek?.join(" & ")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {w.activityPercent}% of this period's activity
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </SectionCard>
  );
}

function ProductSuggestionCard({ suggestion, onCopy, onAddToPlan, isAd }) {
  const hasDiscount = suggestion.discountPrice != null && suggestion.discountPrice < suggestion.price;

  return (
    <Card variant="outlined" sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar src={suggestion.imageUrl || undefined} variant="rounded" sx={{ width: 48, height: 48 }}>
          <Inventory2RoundedIcon fontSize="small" />
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={700} noWrap title={suggestion.name}>
            {suggestion.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {suggestion.storeName}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.25 }}>
            {hasDiscount ? (
              <>
                <Typography variant="body2" fontWeight={800} color="error.main">
                  {formatCurrency(suggestion.discountPrice)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                  {formatCurrency(suggestion.price)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" fontWeight={800}>
                {formatCurrency(suggestion.price)}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {suggestion.reason}
      </Typography>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {suggestion.suggestedPlatforms?.map((p) => (
          <PlatformChip key={p} name={p} />
        ))}
        {isAd && suggestion.suggestedAdBudgetMin != null && (
          <Chip
            size="small"
            label={`Budget ${formatCurrency(suggestion.suggestedAdBudgetMin)}–${formatCurrency(suggestion.suggestedAdBudgetMax)}`}
            color="error"
            variant="outlined"
            sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
          />
        )}
      </Stack>

      <Divider />

      <CaptionBlock label="EN" text={suggestion.captionEn} onCopy={onCopy} />
      <CaptionBlock label="AR" text={suggestion.captionAr} onCopy={onCopy} rtl />

      <Button
        variant="contained"
        size="small"
        startIcon={<AddRoundedIcon />}
        onClick={onAddToPlan}
        sx={{ mt: "auto", alignSelf: "flex-start" }}
      >
        {isAd ? "Add to plan as ad" : "Add to plan"}
      </Button>
    </Card>
  );
}

function CaptionBlock({ label, text, onCopy, rtl }) {
  return (
    <Box sx={{ bgcolor: "action.hover", borderRadius: 1.5, p: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Chip label={label} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 800 }} />
        <Tooltip title="Copy caption">
          <IconButton size="small" onClick={() => onCopy(text)}>
            <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography
        variant="caption"
        sx={{ whiteSpace: "pre-line", display: "block", direction: rtl ? "rtl" : "ltr", textAlign: rtl ? "right" : "left" }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function ContentPlanSection({
  items,
  loading,
  statusFilter,
  platformFilter,
  onStatusFilterChange,
  onPlatformFilterChange,
  onStatusChange,
  onDelete,
  onAddCustom,
}) {
  return (
    <SectionCard
      title="Content plan"
      subtitle="Posts and ads you've queued up, tracked through to posted"
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => onStatusFilterChange(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={Number(value)}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Platform</InputLabel>
            <Select label="Platform" value={platformFilter} onChange={(e) => onPlatformFilterChange(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={Number(value)}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" startIcon={<AddRoundedIcon />} onClick={onAddCustom}>
            Add custom
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState text="Nothing in the content plan yet - add a suggestion above or a custom post." icon={CampaignRoundedIcon} />
      ) : (
        <Stack divider={<Divider />}>
          {items.map((item) => (
            <PlanItemRow key={item.socialMediaPostId} item={item} onStatusChange={onStatusChange} onDelete={onDelete} />
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

function PlanItemRow({ item, onStatusChange, onDelete }) {
  const platformName = PLATFORM_LABELS[item.platform];
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
      <Avatar src={item.productImageUrl || undefined} variant="rounded" sx={{ width: 40, height: 40 }}>
        <Inventory2RoundedIcon fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={700} noWrap>
            {item.productName || "Custom post"}
          </Typography>
          <PlatformChip name={platformName} />
          <Chip size="small" label={POST_TYPE_LABELS[item.postType]} variant="outlined" sx={{ height: 20, fontSize: 10 }} />
          <Chip size="small" label={STATUS_LABELS[item.status]} color={STATUS_COLORS[item.status]} sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {formatDate(item.scheduledFor)}
          {item.adBudget ? ` · Budget ${formatCurrency(item.adBudget)}` : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 480 }}>
          {item.caption}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
        {item.status !== 2 && (
          <Tooltip title="Mark posted">
            <IconButton size="small" color="success" onClick={() => onStatusChange(item, 2)}>
              <CheckCircleRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {item.status === 1 && (
          <Tooltip title="Skip">
            <IconButton size="small" onClick={() => onStatusChange(item, 3)}>
              <BlockRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {item.status !== 1 && (
          <Tooltip title="Back to planned">
            <IconButton size="small" onClick={() => onStatusChange(item, 1)}>
              <ReplayRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => onDelete(item)}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function AddToPlanDialog({ suggestion, defaultPostType = "Post", defaultBudget, onClose, onCreated }) {
  const availablePlatforms = suggestion?.suggestedPlatforms?.length ? suggestion.suggestedPlatforms : ALL_PLATFORMS;
  const [platform, setPlatform] = useState(availablePlatforms[0]);
  const [postType, setPostType] = useState(defaultPostType);
  const [language, setLanguage] = useState("en");
  const [caption, setCaption] = useState(suggestion ? suggestion.captionEn : "");
  const [scheduledFor, setScheduledFor] = useState(() => toLocalDateTimeInputValue(new Date(Date.now() + 60 * 60 * 1000)));
  const [adBudget, setAdBudget] = useState(defaultBudget ?? "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const switchLanguage = (lang) => {
    setLanguage(lang);
    if (suggestion) setCaption(lang === "en" ? suggestion.captionEn : suggestion.captionAr);
  };

  const handleSave = async () => {
    if (!caption.trim()) {
      setError("Caption can't be empty.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createSocialMediaPlanItem({
        productId: suggestion?.productId ?? null,
        platform: PLATFORM_BY_NAME[platform],
        postType: POST_TYPE_BY_NAME[postType],
        caption: caption.trim(),
        scheduledFor: new Date(scheduledFor).toISOString(),
        adBudget: postType === "Ad" && adBudget !== "" ? Number(adBudget) : null,
        reason: suggestion?.reason ?? null,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch {
      setError("Could not save this to the content plan. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{suggestion ? `Add "${suggestion.name}" to the plan` : "Add a custom post"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
              Platform
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={platform} onChange={(e, v) => v && setPlatform(v)}>
              {availablePlatforms.map((p) => (
                <ToggleButton key={p} value={p} sx={{ textTransform: "none", px: 1.5, gap: 0.5 }}>
                  {PLATFORM_ICONS[p] && React.createElement(PLATFORM_ICONS[p], { sx: { fontSize: 16 } })}
                  {p}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel>Post type</InputLabel>
            <Select label="Post type" value={postType} onChange={(e) => setPostType(e.target.value)}>
              {Object.keys(POST_TYPE_BY_NAME).map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {suggestion && (
            <ToggleButtonGroup exclusive size="small" value={language} onChange={(e, v) => v && switchLanguage(v)} sx={{ alignSelf: "flex-start" }}>
              <ToggleButton value="en" sx={{ textTransform: "none", px: 1.5 }}>
                English caption
              </ToggleButton>
              <ToggleButton value="ar" sx={{ textTransform: "none", px: 1.5 }}>
                Arabic caption
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <TextField
            label="Caption"
            multiline
            minRows={5}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            slotProps={{ input: { sx: { direction: language === "ar" ? "rtl" : "ltr" } } }}
            fullWidth
          />

          <TextField
            label="Scheduled for"
            type="datetime-local"
            size="small"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />

          {postType === "Ad" && (
            <TextField
              label="Ad budget (₪)"
              type="number"
              size="small"
              value={adBudget}
              onChange={(e) => setAdBudget(e.target.value)}
              fullWidth
            />
          )}

          <TextField
            label="Notes (internal, optional)"
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Add to plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
